// v1
// Path: frontend/src/components/QuickBooksSync/PipelineConfigModal.jsx
// Inputs: token, config, accounts, dbProfiles, initialDefaults, onClose, onSaved
// Outputs: React JSX modal for creating or editing sync pipeline configurations
// Summary: Configuration modal requiring explicit saved database profile selection and live database discovery to configure entity sync pipelines.

import React, { useState, useEffect } from 'react';

export const PipelineConfigModal = ({ token, config, accounts = [], dbProfiles = [], initialDefaults = {}, onClose, onSaved }) => {
    const [loading, setLoading] = useState(false);
    const [availableDbs, setAvailableDbs] = useState([]);
    const [loadingDbs, setLoadingDbs] = useState(false);
    const [customDbMode, setCustomDbMode] = useState(false);

    const [formData, setFormData] = useState({
        configName: '',
        accountId: '',
        sourceConnPrefix: '',
        sourceDatabaseName: '',
        entityType: 'Customers',
        syncDirection: 'PBS_TO_QB',
        conflictPolicy: 'SOURCE_WINS',
        isActive: true
    });

    const fetchDatabasesForProfile = async (prefix) => {
        if (!prefix) {
            setAvailableDbs([]);
            return;
        }
        setLoadingDbs(true);
        try {
            const res = await fetch(`/api/quickbooks/databases?connPrefix=${encodeURIComponent(prefix)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data)) setAvailableDbs(data);
                else if (data.databases) setAvailableDbs(data.databases);
                else setAvailableDbs([]);
            } else {
                setAvailableDbs([]);
            }
        } catch (err) {
            console.error('[PipelineConfigModal] DB fetch error:', err);
            setAvailableDbs([]);
        } finally {
            setLoadingDbs(false);
        }
    };

    useEffect(() => {
        if (config) {
            const prefix = config.SourceConnPrefix || '';
            setFormData({
                configName: config.ConfigName || '',
                accountId: config.AccountID ? String(config.AccountID) : '',
                sourceConnPrefix: prefix,
                sourceDatabaseName: config.SourceDatabaseName || '',
                entityType: config.EntityType || 'Customers',
                syncDirection: config.SyncDirection || 'PBS_TO_QB',
                conflictPolicy: config.ConflictPolicy || 'SOURCE_WINS',
                isActive: !!config.IsActive
            });
            if (prefix) fetchDatabasesForProfile(prefix);
        } else {
            const prefix = initialDefaults.sourceConnPrefix || (dbProfiles.length === 1 ? dbProfiles[0].connPrefix : '');
            const db = initialDefaults.sourceDatabaseName || '';
            const acc = initialDefaults.accountId ? String(initialDefaults.accountId) : (accounts.length === 1 ? String(accounts[0].AccountID) : '');
            setFormData({
                configName: '',
                accountId: acc,
                sourceConnPrefix: prefix,
                sourceDatabaseName: db,
                entityType: 'Customers',
                syncDirection: 'PBS_TO_QB',
                conflictPolicy: 'SOURCE_WINS',
                isActive: true
            });
            if (prefix) fetchDatabasesForProfile(prefix);
        }
    }, [config, initialDefaults]);

    const handleProfileChange = (prefix) => {
        setFormData(prev => ({
            ...prev,
            sourceConnPrefix: prefix,
            sourceDatabaseName: ''
        }));
        fetchDatabasesForProfile(prefix);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.sourceConnPrefix) {
            alert('Please select a saved Database Profile.');
            return;
        }
        if (!formData.sourceDatabaseName) {
            alert('Please select or enter a Source Database.');
            return;
        }
        if (!formData.accountId) {
            alert('Please select a Target QuickBooks Account.');
            return;
        }

        setLoading(true);
        try {
            const label = formData.configName.trim() || `PBS ${formData.entityType} (${formData.sourceDatabaseName}) -> QB`;
            const payload = {
                ...formData,
                configName: label,
                configId: config ? config.ConfigID : undefined
            };

            const res = await fetch('/api/quickbooks/configs', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to save configuration');
            }

            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            alert(`Save error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full p-6 shadow-2xl space-y-4">
                <div className="flex justify-between items-center border-b border-slate-700 pb-3">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-blue-400">table_chart</span>
                        {config ? 'Edit Table Sync Pipeline' : 'New Table Sync Pipeline'}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                <form onSubmit={handleSave} className="space-y-4">
                    {/* Database Profile */}
                    <div>
                        <label className="block text-xs font-medium text-slate-300 mb-1">
                            Saved Database Profile <span className="text-red-400">*</span>
                        </label>
                        <select
                            required
                            value={formData.sourceConnPrefix}
                            onChange={e => handleProfileChange(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 font-medium"
                        >
                            <option value="" disabled>-- Select a Saved Database Profile * --</option>
                            {dbProfiles.map(p => (
                                <option key={p.connPrefix} value={p.connPrefix}>
                                    {p.displayName}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Database & Account */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="text-xs font-medium text-slate-300">
                                    Database <span className="text-red-400">*</span>
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setCustomDbMode(!customDbMode)}
                                    className="text-[10px] text-blue-400 hover:underline"
                                >
                                    {customDbMode ? 'Select list' : 'Custom'}
                                </button>
                            </div>

                            {customDbMode ? (
                                <input
                                    type="text"
                                    required
                                    placeholder="pbsdata00"
                                    value={formData.sourceDatabaseName}
                                    onChange={e => setFormData({ ...formData, sourceDatabaseName: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-600 rounded px-2.5 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500"
                                />
                            ) : (
                                <select
                                    required
                                    disabled={!formData.sourceConnPrefix || loadingDbs}
                                    value={formData.sourceDatabaseName}
                                    onChange={e => setFormData({ ...formData, sourceDatabaseName: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-600 rounded px-2.5 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500 disabled:opacity-50"
                                >
                                    <option value="" disabled>
                                        {loadingDbs ? 'Loading...' : (!formData.sourceConnPrefix ? 'Pick profile first' : '-- Select DB * --')}
                                    </option>
                                    {availableDbs.map(db => (
                                        <option key={db.DatabaseName} value={db.DatabaseName}>
                                            {db.DatabaseName}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-slate-300 mb-1">
                                Target QB Account <span className="text-red-400">*</span>
                            </label>
                            <select
                                required
                                value={formData.accountId}
                                onChange={e => setFormData({ ...formData, accountId: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-600 rounded px-2.5 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="" disabled>-- Select QB Account * --</option>
                                {accounts.map(a => (
                                    <option key={a.AccountID} value={a.AccountID}>
                                        {a.AccountName} ({a.Environment})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Entity & Label */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-slate-300 mb-1">PBS Table / Entity</label>
                            <select
                                value={formData.entityType}
                                onChange={e => setFormData({ ...formData, entityType: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-600 rounded px-2.5 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="Customers">Customers (CUSFIL)</option>
                                <option value="Items">Inventory Items (ITMFIL)</option>
                                <option value="Accounts">Chart of Accounts (CHARTF)</option>
                                <option value="Vendors">Vendors (VENFIL)</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-slate-300 mb-1">Custom Label (Optional)</label>
                            <input
                                type="text"
                                placeholder={`PBS ${formData.entityType} -> QB`}
                                value={formData.configName}
                                onChange={e => setFormData({ ...formData, configName: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-600 rounded px-2.5 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-slate-300 mb-1">Sync Direction</label>
                            <select
                                value={formData.syncDirection}
                                onChange={e => setFormData({ ...formData, syncDirection: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-600 rounded px-2.5 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="PBS_TO_QB">PBS &rarr; QuickBooks (Push)</option>
                                <option value="QB_TO_PBS">QuickBooks &rarr; PBS (Pull)</option>
                                <option value="BIDIRECTIONAL">Bidirectional Sync</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-300 mb-1">Conflict Policy</label>
                            <select
                                value={formData.conflictPolicy}
                                onChange={e => setFormData({ ...formData, conflictPolicy: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-600 rounded px-2.5 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="SOURCE_WINS">Source (PBS) Wins</option>
                                <option value="TARGET_WINS">Target (QB) Wins</option>
                                <option value="SKIP">Skip on Conflict</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                        <input
                            type="checkbox"
                            id="pipelineActive"
                            checked={formData.isActive}
                            onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                            className="rounded bg-slate-900 border-slate-600 text-blue-600 focus:ring-0 w-4 h-4 cursor-pointer"
                        />
                        <label htmlFor="pipelineActive" className="text-xs text-slate-300">Enable Pipeline for Synchronizations</label>
                    </div>

                    <div className="flex justify-end gap-3 pt-3 border-t border-slate-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !formData.sourceConnPrefix || !formData.sourceDatabaseName || !formData.accountId}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded text-sm font-semibold transition-colors"
                        >
                            {loading ? 'Saving...' : 'Save Pipeline'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
